import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import TourCard from "@/components/TourCard";
import Testimonials from "@/components/Testimonials";
import StoryStrip from "@/components/StoryStrip";
import { ArrowRight, Compass, Map, Truck, Shield, Calendar, Mountain } from "lucide-react";

export default function HomePage() {
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data.tours || [])).catch(() => {});
  }, []);

  const featured = tours.slice(0, 4);

  return (
    <div data-testid="home-page" className="bg-[#FAF6F0]">
      {/* HERO — full-bleed image, headline overlay on left, mini-collage bottom-right */}
      <section className="relative h-screen min-h-[680px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1516497084411-042e90c17be1?w=2400"
          alt="Tanzania safari plains"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full pt-32 md:pt-40 pb-32 flex flex-col">
          <div className="max-w-3xl fade-up">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl text-[#FAF6F0] leading-[1.02] font-light">
              Unleash Your<br />
              <em className="italic font-light">Tanzanian</em> Safari Experience
            </h1>
          </div>

          {/* Bottom: action row + mini collage */}
          <div className="mt-auto grid md:grid-cols-2 gap-8 items-end">
            <div className="bg-[#FAF6F0]/95 backdrop-blur rounded-3xl p-8 max-w-md">
              <p className="text-sm text-[#23170E]/75 leading-relaxed">
                Hand-crafted private safaris through the Serengeti, Ngorongoro Crater and Tarangire —
                ending, if you like, on the sand in Zanzibar.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/tours" data-testid="hero-cta-tours" className="btn-pill btn-pill-primary">
                  Discover More <ArrowRight size={16} />
                </Link>
                <Link to="/tours" data-testid="hero-cta-packages" className="btn-pill btn-pill-light">
                  Our Packages
                </Link>
              </div>
            </div>

            <div className="hidden md:flex items-end justify-end gap-3">
              {[
                "https://images.pexels.com/photos/5859098/pexels-photo-5859098.jpeg?w=400",
                "https://images.unsplash.com/photo-1597877774402-d04cad0b7596?w=400",
                "https://images.pexels.com/photos/20852883/pexels-photo-20852883.jpeg?w=400",
              ].map((src, i) => (
                <div key={i} className={`rounded-2xl overflow-hidden border-2 border-[#FAF6F0]/40 shadow-xl ${i === 1 ? "w-32 h-32" : "w-24 h-24"}`}>
                  <img src={src} alt="Tanzania" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT US — big centered serif intro with stats */}
      <section className="py-24 md:py-36 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <p className="overline">about us</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl italic font-light mt-6 leading-[1.08]">
            We Craft Unforgettable Tanzanian Journeys That Blend <span className="text-[#A6521B]">Wildlife</span>, Culture, and Adventure Into One Seamless Experience
          </h2>
          <Link to="/about" data-testid="home-about-cta" className="inline-flex mt-12 btn-pill btn-pill-outline">
            Learn More
          </Link>
        </div>

        {/* Stats strip */}
        <div className="max-w-5xl mx-auto mt-20 grid grid-cols-3 gap-8 text-center">
          {[
            { v: "12K+", k: "Happy Travelers" },
            { v: "12+", k: "Years Experience" },
            { v: "5", k: "Signature Itineraries" },
          ].map((s) => (
            <div key={s.k}>
              <p className="font-marcellus text-5xl md:text-6xl text-[#A6521B]">{s.v}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-[#23170E]/65 mt-3">{s.k}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OUR PACKAGES */}
      <section className="py-24 md:py-32 bg-[#F1E8DC] px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="overline">our packages</p>
            <h2 className="font-display text-5xl md:text-6xl italic font-light mt-4 leading-tight">
              Choose Your Tanzania Experience
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((t) => (
              <TourCard key={t.id} tour={t} />
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/tours" data-testid="home-view-all-tours" className="btn-pill btn-pill-primary">
              View All Packages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* GUIDE STORIES (Instagram) */}
      <StoryStrip />

      {/* CUSTOM TRIP CTA BANNER */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#FAF6F0]">
        <div className="max-w-6xl mx-auto bg-[#23170E] rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-25 hidden md:block">
            <img src="https://images.unsplash.com/photo-1631646109206-4b5616964f84?w=1200" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#23170E] via-[#23170E]/70 to-transparent" />
          </div>
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 text-[#FAF6F0]">
              <p className="overline" style={{ color: "#C88B35" }}>plan a custom trip</p>
              <h2 className="font-display text-4xl md:text-5xl italic font-light mt-3 leading-tight text-[#FAF6F0]">
                Don't see your dream trip?<br />Let's design it together.
              </h2>
              <p className="mt-5 text-[#FAF6F0]/75 max-w-xl leading-relaxed">
                Answer four quick questions and we'll come back with two or three hand-drawn itineraries within 24 hours — every one handcrafted around your dates, budget, and what excites you.
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <Link to="/custom-trip" data-testid="home-custom-trip-cta" className="btn-pill btn-pill-primary text-base">
                Start Planning <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="overline">why choose us</p>
            <h2 className="font-display text-5xl md:text-6xl italic font-light mt-4 leading-tight">
              Delivering Exceptional Tanzanian Experiences
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Compass, t: "Expert Local Guides", d: "KWS & KATO certified Tanzanian guides with 10+ years on the trail." },
              { icon: Map, t: "Curated Experiences", d: "Every itinerary is tailored — never cookie-cutter, never rushed." },
              { icon: Truck, t: "Premium 4x4 Vehicles", d: "Custom Land Cruisers with pop-up roofs and comfort built for long days." },
              { icon: Shield, t: "Safety First Approach", d: "First-aid trained driver-guides, radios in every vehicle and a 4x4 serviced before each departure." },
            ].map((it) => (
              <div key={it.t} className="bg-[#FFFFFF] rounded-3xl p-8 text-center border border-[#E2D5C3] hover:border-[#A6521B]/40 transition-colors">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#A6521B]/12 flex items-center justify-center mb-5">
                  <it.icon size={26} className="text-[#A6521B]" />
                </div>
                <h3 className="font-marcellus text-xl text-[#23170E]">{it.t}</h3>
                <p className="mt-3 text-sm text-[#23170E]/65 leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-24 md:py-36 px-6 md:px-12 overflow-hidden">
        <img src="https://static.prod-images.emergentagent.com/jobs/4292361d-f97e-4d88-8653-638367e3cc5c/images/92d11616d82411db59174caa5f48509f16c67fd4b2b3bffa21c0f6920926fd3e.jpeg" alt="Tented camp on the Serengeti plains at golden hour" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(31, 21, 14, 0.86)" }} />

        <div className="relative max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20 text-[#FAF6F0]">
            <p className="overline" style={{ color: "#C88B35" }}>how it works</p>
            <h2 className="font-display text-5xl md:text-6xl italic font-light mt-4 leading-tight text-[#FAF6F0]">
              Book Your Safari In Just<br />A Few Simple Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", icon: Compass, t: "Choose Your Package", d: "Browse our hand-picked safaris from three to ten days, or tell us what you're dreaming of." },
              { n: "02", icon: Calendar, t: "Book Your Date", d: "Hit Book Now, tell us your dates and group size — no payment, no account needed." },
              { n: "03", icon: Mountain, t: "Enjoy the Experience", d: "We handle every detail. You bring the binoculars and a sense of wonder." },
            ].map((s) => (
              <div key={s.n} className="text-center text-[#FAF6F0]">
                <p className="overline" style={{ color: "#C88B35" }}>step</p>
                <p className="font-display text-7xl text-[#C88B35] mt-2 leading-none">{s.n}.</p>
                <div className="w-14 h-14 mx-auto rounded-full bg-[#FAF6F0]/10 border border-[#FAF6F0]/25 flex items-center justify-center mt-6">
                  <s.icon size={22} />
                </div>
                <h3 className="font-marcellus text-2xl mt-5 text-[#FAF6F0]">{s.t}</h3>
                <p className="mt-3 text-[#FAF6F0]/75 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />
    </div>
  );
}
